import { Link } from "react-router-dom";

const FOUNDER_IMG = "https://images.pexels.com/photos/34657187/pexels-photo-34657187.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const credentials = [
  { icon: "fa-shield-alt", text: "Licensed Life & Health Insurance Professional" },
  { icon: "fa-chart-bar", text: "Data Analytics | MBA Qualified" },
  { icon: "fa-globe", text: "International Business Graduate" },
  { icon: "fa-briefcase", text: "Business Advisor" },
  { icon: "fa-heart", text: "Wellness Coach" },
  { icon: "fa-project-diagram", text: "PMP Certified & Process Management Expert" },
  { icon: "fa-microphone", text: "Accomplished Speaker & Thought Leader" },
];

const values = [
  { icon: "fa-handshake", title: "Relationship-First", desc: "We build long-term partnerships, not transactional relationships. Your success is our success." },
  { icon: "fa-graduation-cap", title: "Education-Driven", desc: "We empower you with knowledge to make confident, informed decisions for your business." },
  { icon: "fa-bullseye", title: "Results-Oriented", desc: "Every strategy is designed to produce measurable, sustainable outcomes for your business." },
  { icon: "fa-users", title: "Community-Connected", desc: "Our trusted partner network fills every gap — if we don't do it, we know who does." },
];

const timeline = [
  { year: "2020", desc: "Paradigm Global Business Solutions is founded with a vision to change how entrepreneurs access support." },
  { year: "Growth", desc: "Expanded from insurance advisory to full-spectrum business advisory services." },
  { year: "National Reach", desc: "Extended services nationwide and internationally, serving entrepreneurs across borders." },
  { year: "Today", desc: "A comprehensive trusted resource center for business owners with 9+ service areas and 3 core programs." },
];

export const AboutPage = () => {
  return (
    <main data-testid="about-page">
      {/* PAGE HERO */}
      <div className="pg-page-hero">
        <div className="pg-container">
          <span className="pg-label">About Us</span>
          <h1>Our Story & Mission</h1>
          <p>Built by an entrepreneur, for entrepreneurs.</p>
        </div>
      </div>

      {/* ABOUT PARADIGM */}
      <section className="pg-section">
        <div className="pg-container">
          <div className="pg-two-col">
            <div>
              <span className="pg-label">About Us</span>
              <h2>About Paradigm Global Business Solutions</h2>
              <div className="pg-divider left" />
              <p>
                Paradigm Global Business Solutions is a full-service advisory firm dedicated to empowering
                entrepreneurs and corporations to achieve long-term financial and personal success. Through
                education, advocacy, and expert guidance, we deliver the tools and resources needed to build a
                stronger future.
              </p>
              <p>
                We created Paradigm to be the single resource center that combines business strategy,
                insurance solutions, funding assistance, data analytics, and executive coaching into
                one seamless client experience.
              </p>
              <p style={{ fontWeight: 700, color: "var(--magenta)" }}>
                "Shift Your Thinking to Incubate Your Success"
              </p>
            </div>
            <div>
              <div style={{ background: "var(--bg-light)", borderRadius: 16, padding: "2rem" }}>
                <h3 style={{ color: "var(--magenta)", marginBottom: "1rem" }}>Our Vision</h3>
                <p>To be the premier single-source business resource partner — empowering entrepreneurs
                  with everything they need to build, scale, protect, and transition their ventures successfully.</p>
                <div style={{ marginTop: "1.5rem", background: "var(--bg-pink)", borderRadius: 12, padding: "1.2rem" }}>
                  <h4 style={{ fontSize: ".85rem", color: "var(--magenta)", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: ".6rem" }}>Core Values</h4>
                  <ul style={{ display: "flex", flexDirection: "column", gap: ".4rem" }}>
                    {["Integrity First", "Client-Centered", "Excellence Always", "Community Impact"].map((v) => (
                      <li key={v} style={{ display: "flex", alignItems: "center", gap: ".5rem", fontSize: ".9rem", color: "var(--text)" }}>
                        <i className="fas fa-check-circle" style={{ color: "var(--magenta)", fontSize: ".75rem" }} /> {v}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CEO BIO */}
      <section className="pg-section pg-section-light" id="founder">
        <div className="pg-container">
          <div className="pg-founder-grid">
            <div>
              <div className="pg-founder-photo">
                <img src={FOUNDER_IMG} alt="Strategy, Strength & Wealth" style={{ minHeight: 380, objectFit: "cover" }} />
              </div>
              <div className="pg-creds">
                {credentials.map((c) => (
                  <div className="pg-cred" key={c.text}>
                    <i className={`fas ${c.icon}`} /> {c.text}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <span className="pg-label">Our CEO</span>
              <h2>Ekeyah Wallace</h2>
              <p style={{ fontSize: ".95rem", color: "var(--magenta)", fontWeight: 600, marginBottom: "1.2rem" }}>
                CEO & Founder | Paradigm Global Business Solutions
              </p>
              <p>
                Ekeyah Wallace is an accomplished speaker and leader whose expertise in business and health
                has captivated audiences across the continent. Her unique story and dynamic presentations on
                financial literacy, mental wellness, and motivation leave a lasting impact on every audience she
                addresses.
              </p>
              <p>
                Whether speaking on motivation, mental wellness, or financial management,
                Ekeyah's talks are tailored to meet the specific needs of every audience — and her passion for
                helping others is evident in every word spoken.
              </p>
              <p>
                Every client works directly with Ekeyah — no account managers, no runaround.
                This personalized approach ensures that each business receives the tailored attention
                and expertise it deserves.
              </p>
              <div className="pg-fq">
                "I didn't build Paradigm to be another consulting company. I built it to be
                the resource center I wish I had when I started my own journey. Every entrepreneur
                deserves a partner who truly cares about their success."
              </div>
              <Link to="/consultation" className="pg-btn pg-btn-primary">
                <i className="fas fa-calendar-check" /> BOOK A CALL WITH OUR PARADIGM GLOBAL TEAM
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="pg-section">
        <div className="pg-container">
          <div className="pg-text-center">
            <span className="pg-label">What Drives Us</span>
            <h2>Our Core Values</h2>
            <div className="pg-divider" />
          </div>
          <div className="pg-why-grid">
            {values.map((v) => (
              <div className="pg-why-card" key={v.title}>
                <span className="ico"><i className={`fas ${v.icon}`} /></span>
                <h4>{v.title}</h4>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="pg-section pg-section-light">
        <div className="pg-container">
          <div className="pg-text-center">
            <span className="pg-label">Our Journey</span>
            <h2>The Paradigm Story</h2>
            <div className="pg-divider" />
          </div>
          <div style={{ maxWidth: 600, margin: "0 auto" }}>
            {timeline.map((t, i) => (
              <div key={t.year} style={{ display: "flex", gap: "1.5rem", marginBottom: "2rem", position: "relative" }}>
                <div style={{
                  width: 50, height: 50, borderRadius: "50%",
                  background: "var(--grad)", display: "flex", alignItems: "center",
                  justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: ".85rem",
                  flexShrink: 0
                }}>
                  {i + 1}
                </div>
                <div>
                  <h4 style={{ color: "var(--magenta)", marginBottom: ".3rem" }}>{t.year}</h4>
                  <p style={{ color: "var(--muted)", fontSize: ".9rem", margin: 0 }}>{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pg-cta-banner">
        <div className="pg-container">
          <h2>Ready to Partner with Paradigm?</h2>
          <p>Book a free strategy call and discover how we can support your entrepreneurial journey.</p>
          <div className="pg-cta-btns">
            <Link to="/consultation" className="pg-btn pg-btn-white">
              <i className="fas fa-calendar-check" /> Book a Call
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
